import { useId } from "react";
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

interface FieldFrameProps {
  children: ReactNode;
  className?: string;
  error?: string;
  hint?: string;
  id: string;
  label: string;
  required?: boolean;
}

function FieldFrame({ children, className, error, hint, id, label, required }: FieldFrameProps) {
  return (
    <div className={["ui-field", className].filter(Boolean).join(" ")}>
      <label className="ui-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span aria-hidden="true" className="ui-field__required">
            {" "}*
          </span>
        ) : null}
      </label>
      {children}
      {hint ? (
        <p className="ui-field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="ui-field__error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function describedBy(id: string, hint?: string, error?: string): string | undefined {
  const ids = [hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean);
  return ids.length ? ids.join(" ") : undefined;
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  id?: string;
  name: string;
  label: string;
  hint?: string;
  error?: string;
  fieldClassName?: string;
}

export function TextField({
  id: suppliedId,
  name,
  label,
  hint,
  error,
  fieldClassName,
  className,
  autoComplete = "off",
  required,
  ...props
}: TextFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? `${name}-${generatedId}`;

  return (
    <FieldFrame
      className={fieldClassName}
      error={error}
      hint={hint}
      id={id}
      label={label}
      required={required}
    >
      <input
        {...props}
        aria-describedby={describedBy(id, hint, error)}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        autoComplete={autoComplete}
        className={["ui-field__control", className].filter(Boolean).join(" ")}
        id={id}
        name={name}
        required={required}
      />
    </FieldFrame>
  );
}

export interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  id?: string;
  name: string;
  label: string;
  hint?: string;
  error?: string;
  fieldClassName?: string;
}

export function TextAreaField({
  id: suppliedId,
  name,
  label,
  hint,
  error,
  fieldClassName,
  className,
  autoComplete = "off",
  required,
  ...props
}: TextAreaFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? `${name}-${generatedId}`;

  return (
    <FieldFrame
      className={fieldClassName}
      error={error}
      hint={hint}
      id={id}
      label={label}
      required={required}
    >
      <textarea
        {...props}
        aria-describedby={describedBy(id, hint, error)}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        autoComplete={autoComplete}
        className={["ui-field__control", className].filter(Boolean).join(" ")}
        id={id}
        name={name}
        required={required}
      />
    </FieldFrame>
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  id?: string;
  name: string;
  label: string;
  hint?: string;
  error?: string;
  fieldClassName?: string;
}

export function SelectField({
  id: suppliedId,
  name,
  label,
  hint,
  error,
  fieldClassName,
  className,
  required,
  children,
  ...props
}: SelectFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? `${name}-${generatedId}`;

  return (
    <FieldFrame
      className={fieldClassName}
      error={error}
      hint={hint}
      id={id}
      label={label}
      required={required}
    >
      <select
        {...props}
        aria-describedby={describedBy(id, hint, error)}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        className={["ui-field__control", className].filter(Boolean).join(" ")}
        id={id}
        name={name}
        required={required}
      >
        {children}
      </select>
    </FieldFrame>
  );
}

export interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "type"> {
  id?: string;
  label: ReactNode;
  description?: ReactNode;
}

export function CheckboxField({
  id: suppliedId,
  name,
  label,
  description,
  className,
  ...props
}: CheckboxFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? `${name ?? "checkbox"}-${generatedId}`;

  return (
    <label className={["ui-checkbox", className].filter(Boolean).join(" ")} htmlFor={id}>
      <input {...props} id={id} name={name} type="checkbox" />
      <span className="ui-checkbox__copy">
        <span className="ui-checkbox__label">{label}</span>
        {description ? <span className="ui-checkbox__description">{description}</span> : null}
      </span>
    </label>
  );
}
